import type { ViewModelId, ViewJSON } from 'balus-common';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { RTDBPath } from 'balus-common';
import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import { PositionSet } from '@view-model/models/common/PositionSet';
import type { Position } from '@view-model/models/common/types/ui';
import { useEffect, useMemo } from 'react';
import { viewModelPageContentAtom } from '@user/pages/ViewModelPage/viewModelPageContentAtom';
import { getDefaultStore } from 'jotai';
import { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { toast } from 'react-hot-toast';
import { throttle } from 'lodash-es';
interface Props {
  viewModelId: ViewModelId;
}

export const ViewModelPageContentsLoader = ({ viewModelId }: Props) => {
  const { t } = useTranslation();
  const onError = useMemo(
    () =>
      // NOTE: permission errorが出る時には、このコンポーネント内の全部のuseSnapshotのonErrorが呼ばれるためthrottleしている
      throttle(() => {
        toast.error(t('common.message.loadFailed'));
      }, 1000),
    [t]
  );

  const [viewPositionSet] = useSnapshot({
    path: RTDBPath.View.positionsPath(viewModelId),
    load({ getChildValues }) {
      return PositionSet.load(getChildValues() as Record<string, Position> | null);
    },
    onError,
  });

  const [viewEntities] = useSnapshot({
    path: RTDBPath.View.viewsPath(viewModelId),
    load: ({ getChildValues }) => {
      return Object.values(getChildValues() as Record<string, ViewJSON>).map((json) => ViewEntity.load(json));
    },
    onError,
  });

  useEffect(() => {
    return () => {
      getDefaultStore().set(viewModelPageContentAtom, (prev) => {
        const newRecord = { ...prev.viewContentsRecord };
        delete newRecord[viewModelId];
        return { viewContentsRecord: newRecord };
      });
    };
  }, [viewModelId]);

  useEffect(() => {
    if (!viewEntities || !viewPositionSet) {
      return;
    }

    const viewContents = new ViewCollectionContents(new ViewCollection(viewEntities), viewPositionSet);

    getDefaultStore().set(viewModelPageContentAtom, (prev) => ({
      viewContentsRecord: { ...prev.viewContentsRecord, [viewModelId]: viewContents },
    }));
  }, [viewEntities, viewPositionSet, viewModelId]);

  return null;
};
